"use client";

import Link from "next/link";
import { toast } from "sonner";
import { useState, createRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { cn, withUrl } from "@/lib/utils";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import PrimaryButton from "@/components/ui/primary-button";
import Testimonial from "@/components/ui/testimonial";
import Select from "@/components/ui/select/select";
import LINKS from "@/lib/links";
import { submitApplication } from "./careers-application-form-actions";
import { useWindowSize } from "@/hooks/use-window-size";
import Ringer from "../ringer";
import CareersApplicationFormSuccess from "./careers-application-form-success";
import ReCAPTCHA from "react-google-recaptcha";

export interface CareersApplicationFormProps {
  className?: string;
  id?: string;
  heading?: string;
  subheading?: string;
  testimonialCopy?: string;
  testimonials?: {
    image?: string;
    name: string;
    date: string;
    stars: number;
    text: string;
  }[];
}

// Job title options with proper typing
type JobTitleOption = {
  value: string;
  label: string;
};

// Define the form schema using Zod
const formSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  phoneNumber: z.string().min(1, "Phone number is required"),
  zipCode: z
    .string()
    .min(1, "Zip code is required")
    .regex(/^\d{1,5}$/, "Zip code must be 5 digits or less"),
  isZipCodeValid: z.boolean(),
  jobTitle: z.string().optional(),
  message: z.string().optional(),
});

// Type for the form values based on the schema
type FormValues = z.infer<typeof formSchema>;

// Extended type that includes recaptchaToken for API submission
type FormSubmissionData = FormValues & {
  recaptchaToken: string;
};

const defaultTestimonials = [
  {
    image: undefined,
    name: "Marie J.",
    date: "OCT 2024",
    stars: 5,
    text: "I have been working for Care Indeed for many years. It's a great company that provides premium care to their clients. They provide a flexible schedule for their employees.",
  },
  {
    image: undefined,
    name: "Rashmi M.",
    date: "SEP 2024",
    stars: 5,
    text: "I've chosen to be happy. I'm happy to serve the elderly citizens who need my help. The Care Indeed gave me the golden opportunity to make senior citizens happy. Care Indeed is a great place to work ❤️.",
  },
];

const CareersApplicationForm: React.FC<CareersApplicationFormProps> = ({
  className,
  id,
  heading = "Ready to Apply?",
  subheading = "Fill out the form to start the process.",
  testimonialCopy = "We've had more than 10,000 caregivers and nurses in 14 years",
  testimonials = defaultTestimonials,
}) => {
  const size = useWindowSize();
  // Use useState only for the manual success reset functionality
  const [isCustomSuccess, setIsCustomSuccess] = useState(false);
  const recaptchaRef = createRef<ReCAPTCHA>();
  const recaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_CLIENT_KEY;
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const handleRecaptchaChange = setRecaptchaToken;

  // Set up React Hook Form with Zod validation
  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      zipCode: "",
      isZipCodeValid: false,
      jobTitle: "",
      message: "",
    },
  });

  // Handle form submission
  const onSubmit = async (data: FormValues) => {
    try {
      console.log("Form data submitted:", data);

      // Check if reCAPTCHA is completed
      if (!recaptchaToken) {
        toast.error("Please complete the CAPTCHA verification");
        // Set a non-field error to prevent form from being considered successful
        setError("root.recaptcha", {
          type: "manual",
          message: "Please complete the CAPTCHA verification",
        });
        return; // Exit early without setting success state
      }

      const submissionData: FormSubmissionData = {
        ...data,
        recaptchaToken,
      };

      const result = await submitApplication(submissionData);

      if (result.status === "success") {
        // Set manual success state for clear control
        setIsCustomSuccess(true);
        toast.success(result.message || "Application submitted successfully!");
        // Reset form after successful submission
        reset();
        // Reset reCAPTCHA
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setRecaptchaToken(null);
      } else {
        // Ensure we don't show success state on error
        setIsCustomSuccess(false);
        toast.error(
          result.message || "There was an error submitting your application"
        );
      }
    } catch (error) {
      // Ensure we don't show success state on error
      setIsCustomSuccess(false);
      toast.error("An unexpected error occurred. Please try again.");
      console.error("Form submission error:", error);
    }
  };

  // Handle form reset
  const handleFormReset = () => {
    reset(undefined, {
      keepIsSubmitSuccessful: false,
    });
    setIsCustomSuccess(false);
  };

  // Only use our explicit success state for determining UI state
  // This gives us more control than relying on React Hook Form's isSubmitSuccessful
  const isSuccess = isCustomSuccess;

  // Job title options
  const jobTitles: JobTitleOption[] = [
    {
      value: "Certified Nursing Assistant (CNA)",
      label: "Certified Nursing Assistant (CNA)",
    },
    { value: "Home Care Aide", label: "Home Care Aide" },
    {
      value: "Licensed Vocational Nurse (LVN)",
      label: "Licensed Vocational Nurse (LVN)",
    },
    { value: "Registered Nurse (RN)", label: "Registered Nurse (RN)" },
    { value: "Office Employee", label: "Office Employee" },
    { value: "Other", label: "Other" },
  ];

  return (
    <section
      id={id}
      aria-label={id}
      className={`bg-primary-100 ${className}`}
    >
      <div className="p-4 md:px-6 md:py-8 lg:mx-auto lg:max-w-lg lg:px-3 lg:py-10">
        <div className="flex flex-col gap-4 md:gap-6 lg:grid lg:grid-cols-2 lg:gap-4 lg:gap-x-8 lg:gap-y-0">
          {(size === "lg" || !isSuccess) && (
            <div className="order-1 flex h-fit flex-col lg:col-start-1 lg:row-start-1 lg:mb-0">
              <h2 className="font-montserrat text-heading-4 text-secondary-500 lg:text-heading-2">
                {heading}
              </h2>
              <p className="mt-2 font-montserrat text-subheading-3-400 text-neutral-500 md:mt-3 lg:mt-4 lg:text-subheading-2">
                {subheading}
              </p>

              {/* Exact spacer - only visible on desktop */}
              <div className="hidden h-[56px] lg:block"></div>

              {size === "lg" && (
                <div className="block">
                  {/** 10,000 caregivers text */}
                  <div className="mb-4">
                    <p className="font-montserrat text-subheading-3-500 text-neutral-600 lg:text-heading-3 lg:!font-medium">
                      {testimonialCopy}
                    </p>
                  </div>

                  {/** Testimonials */}
                  <div>
                    <div className="flex flex-col gap-4">
                      {testimonials?.map((testimonial) => (
                        <Testimonial
                          key={testimonial.name}
                          variant="stars"
                          className="bg-white"
                          imageURL={testimonial.image}
                          name={testimonial.name}
                          date={testimonial.date}
                          stars={testimonial.stars}
                        >
                          {testimonial.text}
                        </Testimonial>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {size !== "lg" && (
            <>
              {/* Mobile/Tablet version of testimonials - hidden on desktop */}
              <div className="order-3 h-fit">
                {/** 10,000 caregivers text */}
                <div className="mb-4 md:mb-3">
                  <p className="font-montserrat text-subheading-3-500 text-neutral-600 md:text-heading-4 lg:text-heading-3 lg:!font-medium">
                    {testimonialCopy}
                  </p>
                </div>

                {/** Testimonials */}
                <div>
                  <div className="flex flex-col gap-4 md:gap-3">
                    {testimonials?.map((testimonial) => (
                      <Testimonial
                        key={testimonial.name}
                        className="bg-white"
                        imageURL={testimonial.image}
                        name={testimonial.name}
                        date={testimonial.date}
                        stars={testimonial.stars}
                      >
                        {testimonial.text}
                      </Testimonial>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="order-2 h-fit lg:col-start-2 lg:row-span-2 lg:row-start-1">
            <div
              className={cn(
                "flex min-h-[384px] flex-col border border-[#FAF8F8] py-2 md:rounded-[24px] md:bg-white md:p-6 md:shadow-[4px_8px_24px_2px_rgba(82,77,75,0.12)] lg:min-h-[624px]",
                isSubmitting && "items-center justify-center"
              )}
            >
              {isSubmitting ? (
                <div className="m-auto flex h-full min-h-[400px] items-center justify-center">
                  <Ringer
                    size={60}
                    color="teal"
                    colorShade="500"
                  />
                </div>
              ) : isSuccess === true ? (
                <CareersApplicationFormSuccess
                  onSubmitAnotherApplication={handleFormReset}
                />
              ) : (
                <>
                  <div className="flex flex-col gap-2">
                    <h3 className="font-montserrat text-subheading-3-500 text-secondary-500 md:text-heading-3">
                      Application Form
                    </h3>
                    <p className="font-roboto text-body-1-300 text-neutral-500">
                      All required fields are marked with an asterisk.
                    </p>
                  </div>
                  <form
                    className="mt-4 flex flex-col gap-4"
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Input
                        color="white"
                        id="first-name"
                        label="First Name"
                        autoComplete="given-name"
                        required
                        placeholder="First Name *"
                        error={!!errors.firstName}
                        errorMessage={errors.firstName?.message}
                        {...register("firstName")}
                      />

                      <Input
                        color="white"
                        id="last-name"
                        label="Last Name"
                        autoComplete="family-name"
                        required
                        placeholder="Last Name *"
                        error={!!errors.lastName}
                        errorMessage={errors.lastName?.message}
                        {...register("lastName")}
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Input
                        color="white"
                        id="email"
                        label="Email Address"
                        type="email"
                        autoComplete="email"
                        required
                        placeholder="Email Address *"
                        error={!!errors.email}
                        errorMessage={errors.email?.message}
                        {...register("email")}
                      />

                      <Input
                        color="white"
                        id="phone"
                        label="Phone Number"
                        autoComplete="tel"
                        required
                        placeholder="Phone Number *"
                        error={!!errors.phoneNumber}
                        errorMessage={errors.phoneNumber?.message}
                        {...register("phoneNumber")}
                      />
                    </div>

                    <div>
                      <Input
                        color="white"
                        id="zip-code"
                        label="Zip Code"
                        autoComplete="postal-code"
                        required
                        placeholder="Zip Code *"
                        error={!!errors.zipCode}
                        errorMessage={errors.zipCode?.message}
                        {...register("zipCode")}
                      />
                    </div>

                    <div>
                      <Controller
                        control={control}
                        name="jobTitle"
                        render={({ field }) => (
                          <Select
                            id="job-title"
                            label="Job Title (Optional)"
                            placeholder="Job Title (Optional)"
                            items={jobTitles}
                            value={field.value}
                            onValueChange={field.onChange}
                          />
                        )}
                      />
                    </div>

                    <div>
                      <Textarea
                        id="message"
                        label="Message (Optional)"
                        rows={3}
                        placeholder="Message (Optional)"
                        error={!!errors.message}
                        errorMessage={errors.message?.message}
                        {...register("message")}
                      />
                    </div>

                    <div>
                      <p className="font-roboto text-body-1-300 text-neutral-400">
                        By submitting, you agree to Care Indeed&apos;s{" "}
                        <Link
                          href={withUrl(LINKS.termsOfUse.href)}
                          prefetch
                          className="underline"
                        >
                          Terms of Use
                        </Link>{" "}
                        and{" "}
                        <Link
                          href={withUrl(LINKS.privacyPolicy.href)}
                          prefetch
                          className="underline"
                        >
                          Privacy Policy
                        </Link>
                        .
                      </p>
                    </div>

                    <div>
                      {recaptchaKey && (
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={recaptchaKey}
                          onChange={handleRecaptchaChange}
                        />
                      )}
                    </div>

                    <div>
                      <PrimaryButton
                        color="teal"
                        size="md"
                        disabled={isSubmitting}
                        type="submit"
                        className="w-fit"
                      >
                        Submit Application
                      </PrimaryButton>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CareersApplicationForm;
